<template>
  <div class="marketing-page news-manager">
    <MarketingToolHeader
      eyebrow="Content Marketing"
      title="News"
      description="Plane Mitteilungen, steuere ihre Laufzeit und verteile Inhalte gezielt an die passenden Studios."
      icon="mdi-newspaper-variant-outline">
      <template v-slot:actions>
        <v-btn icon class="icon-action" :loading="loading" aria-label="News aktualisieren" title="News aktualisieren" @click="init">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <v-btn color="#009640" depressed class="primary-action" @click="addNews">
          <v-icon left>mdi-plus</v-icon>
          Neue News
        </v-btn>
      </template>
    </MarketingToolHeader>

    <section class="metric-grid" aria-label="News Übersicht">
      <v-card dark flat class="metric-card">
        <span class="metric-icon metric-icon-green"><v-icon>mdi-broadcast</v-icon></span>
        <div>
          <span>Aktiv</span><strong>{{ loading ? "–" : sortedNews.active.length }}</strong
          ><small>aktuell sichtbar</small>
        </div>
      </v-card>
      <v-card dark flat class="metric-card">
        <span class="metric-icon metric-icon-amber"><v-icon>mdi-clock-outline</v-icon></span>
        <div>
          <span>Geplant</span><strong>{{ loading ? "–" : sortedNews.upcoming.length }}</strong
          ><small>starten später</small>
        </div>
      </v-card>
      <v-card dark flat class="metric-card">
        <span class="metric-icon"><v-icon>mdi-archive-outline</v-icon></span>
        <div>
          <span>Archiv</span><strong>{{ loading ? "–" : sortedNews.past.length }}</strong
          ><small>abgelaufene News</small>
        </div>
      </v-card>
      <v-card dark flat class="metric-card">
        <span class="metric-icon"><v-icon>mdi-format-list-bulleted</v-icon></span>
        <div>
          <span>Gesamt</span><strong>{{ loading ? "–" : news.length }}</strong
          ><small>alle Mitteilungen</small>
        </div>
      </v-card>
    </section>

    <v-card dark flat class="content-surface">
      <div class="surface-header">
        <div>
          <div class="surface-eyebrow">Publikationsplan</div>
          <h2>Aktuell &amp; geplant</h2>
          <p>Alle sichtbaren und vorbereiteten News in zeitlicher Reihenfolge.</p>
        </div>
        <span class="count-pill">{{ sortedNews.active.length + sortedNews.upcoming.length }} Einträge</span>
      </div>

      <div v-if="sortedNews.active.length || sortedNews.upcoming.length" class="content-grid">
        <v-card
          v-for="item in [...sortedNews.active, ...sortedNews.upcoming]"
          :key="item._id"
          dark
          flat
          class="content-card"
          role="button"
          tabindex="0"
          @click="editNews(item)"
          @keydown.enter="editNews(item)">
          <div class="card-media">
            <v-img v-if="item.foto" :src="'/img/news/' + item.foto" height="190" class="content-image" />
            <div v-else class="image-placeholder"><v-icon>mdi-image-off-outline</v-icon></div>
            <span class="status-pill" :class="item.status.colorClass">
              <v-icon small>{{ item.status.icon }}</v-icon>
              {{ item.status.text }}
            </span>
          </div>
          <div class="card-content">
            <h3>{{ trimLength(item.titel, 52) }}</h3>
            <div class="studio-chips">
              <span v-for="studioEntry in (item.studios || []).slice(0, 3)" :key="studioEntry._id || studioEntry" class="mini-chip">{{
                getStudioName(studioEntry)
              }}</span>
              <span v-if="(item.studios || []).length > 3" class="mini-chip">+{{ item.studios.length - 3 }}</span>
            </div>
            <div class="card-meta">
              <span><v-icon small>mdi-calendar-range-outline</v-icon>{{ formatDate(item.von) }} – {{ formatDate(item.bis) }}</span>
              <v-icon small>mdi-arrow-right</v-icon>
            </div>
          </div>
        </v-card>
      </div>

      <div v-else-if="!loading" class="surface-empty">
        <span><v-icon>mdi-calendar-blank-outline</v-icon></span>
        <h3>Keine laufenden News</h3>
        <p>Erstelle eine neue Mitteilung oder plane den nächsten Inhalt.</p>
        <v-btn outlined color="#58d388" class="secondary-action" @click="addNews"><v-icon left>mdi-plus</v-icon>News erstellen</v-btn>
      </div>
    </v-card>

    <v-card v-if="sortedNews.past.length" dark flat class="content-surface archive-surface">
      <div class="surface-header">
        <div>
          <div class="surface-eyebrow">Historie</div>
          <h2>Archiv</h2>
          <p>Abgelaufene Inhalte bleiben für spätere Wiederverwendung erreichbar.</p>
        </div>
        <span class="count-pill">{{ sortedNews.past.length }} archiviert</span>
      </div>

      <div class="archive-grid">
        <v-card
          v-for="item in paginatedArchive"
          :key="item._id"
          dark
          flat
          class="archive-card"
          role="button"
          tabindex="0"
          @click="editNews(item)"
          @keydown.enter="editNews(item)">
          <v-img v-if="item.foto" :src="'/img/news/' + item.foto" height="118" class="archive-image" />
          <div v-else class="archive-placeholder"><v-icon>mdi-image-off-outline</v-icon></div>
          <div class="archive-copy">
            <span class="archive-label"><v-icon small>mdi-archive-outline</v-icon>Archiviert</span>
            <h3>{{ trimLength(item.titel, 42) }}</h3>
          </div>
        </v-card>
      </div>
      <v-pagination v-if="archivePageCount > 1" v-model="archivePage" :length="archivePageCount" class="archive-pagination" dark color="#009640" />
    </v-card>

    <v-dialog
      v-model="newsDialog"
      max-width="960px"
      persistent
      scrollable
      :fullscreen="$vuetify.breakpoint.xsOnly"
      content-class="marketing-editor-dialog">
      <v-card v-if="editNewsItem" dark class="editor-card">
        <div class="editor-header">
          <span class="editor-mark"><v-icon>mdi-newspaper-variant-outline</v-icon></span>
          <div>
            <div class="surface-eyebrow">News Editor</div>
            <h2>{{ editNewsItem._id ? "News bearbeiten" : "Neue News erstellen" }}</h2>
            <p>Inhalt, Bild, Zeitraum und Zielstudios festlegen.</p>
          </div>
          <v-btn icon class="icon-action editor-close" aria-label="Dialog schliessen" @click="newsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <v-card-text class="editor-body">
          <v-row>
            <v-col cols="12" md="7">
              <section class="editor-section">
                <div class="section-heading">
                  <v-icon>mdi-text-box-outline</v-icon>
                  <div>
                    <h3>Inhalt</h3>
                    <p>Text und Darstellung der Mitteilung</p>
                  </div>
                </div>
                <v-text-field v-model="editNewsItem.titel" label="Titel" outlined dark class="modern-field" hide-details="auto" />
                <v-checkbox v-model="editNewsItem.showTitle" label="Titel in der News anzeigen" color="#009640" class="editor-checkbox" />
                <marketing-wysiwyg v-model="editNewsItem.inhalt" :editor-toolbar="customToolbar" />
              </section>
            </v-col>
            <v-col cols="12" md="5">
              <section class="editor-section side-section">
                <div class="section-heading">
                  <v-icon>mdi-image-outline</v-icon>
                  <div>
                    <h3>Bild &amp; Laufzeit</h3>
                    <p>Quadratisches Motiv und Sichtbarkeit</p>
                  </div>
                </div>
                <slim-cropper :key="editNewsItem.foto" class="image-cropper" :options="slimOptionsExercise">
                  <img v-if="editNewsItem.foto" :src="'/img/news/' + editNewsItem.foto" alt="News Bild" />
                  <input type="file" accept="image/*" />
                </slim-cropper>
                <span class="format-hint">Empfohlenes Format: 1:1 · 800 × 800 px</span>
                <v-date-picker v-model="dates" locale="de-DE" range no-title dark full-width color="#009640" class="date-picker" />
              </section>
            </v-col>
            <v-col cols="12">
              <section class="editor-section compact-section">
                <div class="section-heading">
                  <v-icon>mdi-map-marker-multiple-outline</v-icon>
                  <div>
                    <h3>Zielstudios</h3>
                    <p>Wo soll die News sichtbar sein?</p>
                  </div>
                </div>
                <v-select
                  v-model="editNewsItem.studios"
                  :items="studios"
                  item-text="name"
                  item-value="_id"
                  label="Studios auswählen"
                  multiple
                  chips
                  outlined
                  dark
                  class="modern-field"
                  hide-details="auto" />
              </section>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="editor-actions">
          <span class="validation-hint">Titel, Zeitraum und mindestens ein Studio sind erforderlich.</span>
          <v-spacer />
          <v-btn text class="cancel-action" @click="newsDialog = false">Abbrechen</v-btn>
          <v-btn color="#009640" depressed class="primary-action" :disabled="!isFormValid" :loading="saving" @click="saveNews">
            <v-icon left>mdi-content-save-outline</v-icon>Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "../../../api"
import Slim from "../../../plugins/slimImage/slim/slim.vue"
import MarketingToolHeader from "./MarketingToolHeader.vue"
import MarketingWysiwyg from "./MarketingWysiwyg.vue"

export default {
  components: { MarketingToolHeader, "marketing-wysiwyg": MarketingWysiwyg, "slim-cropper": Slim },
  data() {
    return {
      loading: false,
      saving: false,
      newsDialog: false,
      news: [],
      editNewsItem: null,
      dates: [],
      studios: [],
      archivePage: 1,
      archiveItemsPerPage: 8,
      slimOptionsExercise: {
        ratio: "1:1",
        initialImage: "/img/einmalig/imageUploadPlaceholder.jpg",
        buttonCancelLabel: "Abbrechen",
        buttonConfirmLabel: "Bestätigen",
        label: "Bild hochladen",
        service: this.uploadPicture,
        serviceFormat: "file",
        instantEdit: true,
        push: true,
      },
      customToolbar: [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"]],
    }
  },
  computed: {
    sortedNews() {
      const categories = { active: [], upcoming: [], past: [] }
      if (!this.news) return categories
      const sortedByDate = [...this.news].sort((a, b) => b.von - a.von)

      sortedByDate.forEach((item) => {
        const now = this.$moment()
        const from = this.$moment.unix(item.von)
        const to = this.$moment.unix(item.bis)
        let status

        if (now.isAfter(to)) status = "past"
        else if (now.isBefore(from)) status = "upcoming"
        else status = "active"

        categories[status].push({ ...item, status: this.getStatusDetails(status, from) })
      })
      return categories
    },
    paginatedArchive() {
      const start = (this.archivePage - 1) * this.archiveItemsPerPage
      return this.sortedNews.past.slice(start, start + this.archiveItemsPerPage)
    },
    archivePageCount() {
      return Math.ceil(this.sortedNews.past.length / this.archiveItemsPerPage)
    },
    isFormValid() {
      if (!this.editNewsItem) return false
      const { titel, studios, von, bis } = this.editNewsItem
      return titel && titel.length > 2 && studios && studios.length > 0 && von && bis
    },
  },
  created() {
    this.init()
  },
  methods: {
    async init() {
      this.loading = true
      try {
        this.news = await api.getAllNews()
        this.studios = await api.fetchStudios()
        this.$emit("finishedloading")
      } catch (e) {
        this.$toast.error("Fehler beim Laden der Daten.")
      } finally {
        this.loading = false
      }
    },
    getStatusDetails(status, from) {
      const now = this.$moment()
      switch (status) {
        case "active":
          return { text: "Aktiv", icon: "mdi-broadcast", colorClass: "is-visible" }
        case "upcoming": {
          const diff = from.diff(now, "days") + 1
          return { text: `Startet in ${diff} Tag(en)`, icon: "mdi-clock-start", colorClass: "is-upcoming" }
        }
        default:
          return { text: "Archiviert", icon: "mdi-archive-outline", colorClass: "is-past" }
      }
    },
    getStudioName(studioEntry) {
      if (studioEntry && typeof studioEntry === "object" && studioEntry.name) return studioEntry.name
      const studioId = studioEntry && typeof studioEntry === "object" ? studioEntry._id : studioEntry
      const studio = this.studios.find((item) => item._id === studioId)
      return studio ? studio.name : "N/A"
    },
    addNews() {
      this.dates = []
      this.editNewsItem = { _id: null, titel: "", inhalt: "", foto: null, von: null, bis: null, showTitle: true, studios: [] }
      this.newsDialog = true
    },
    editNews(item) {
      this.editNewsItem = JSON.parse(JSON.stringify(item))
      delete this.editNewsItem.status
      this.editNewsItem.studios = (this.editNewsItem.studios || []).map((studio) => studio._id || studio)
      this.dates = [this.$moment.unix(this.editNewsItem.von).format("YYYY-MM-DD"), this.$moment.unix(this.editNewsItem.bis).format("YYYY-MM-DD")]
      this.newsDialog = true
    },
    async saveNews() {
      if (!this.isFormValid) {
        this.$toast.error("Bitte fülle alle Felder aus (Titel, Studios, Datum).")
        return
      }
      this.saving = true
      try {
        await api.saveNews(this.editNewsItem)
        this.$toast.success("News erfolgreich gespeichert")
        this.newsDialog = false
        await this.init()
      } catch (e) {
        this.$toast.error("Fehler beim Speichern der News")
      } finally {
        this.saving = false
      }
    },
    uploadPicture(formdata, progress, success, failure) {
      const formData = new FormData()
      formData.append("file", formdata[0])
      api
        .uploadImage({ art: "news", formData })
        .then((data) => {
          if (data.status) {
            this.editNewsItem.foto = "uploads/" + data.name
            success(data)
          }
        })
        .catch((error) => failure(error))
    },
    trimLength(value, length) {
      if (!value) return ""
      return value.length > length ? value.substring(0, length) + "..." : value
    },
    formatDate(timestamp) {
      return this.$moment.unix(timestamp).format("DD.MM.YY")
    },
  },
  watch: {
    dates(newDates) {
      if (!this.editNewsItem || !newDates || newDates.length < 2) return
      const sortedDates = [...newDates].sort((a, b) => new Date(a) - new Date(b))
      const newFrom = this.$moment(sortedDates[0]).startOf("day").unix()
      const newTo = this.$moment(sortedDates[1]).endOf("day").unix()
      if (this.editNewsItem.von !== newFrom || this.editNewsItem.bis !== newTo) {
        this.editNewsItem.von = newFrom
        this.editNewsItem.bis = newTo
      }
    },
  },
}
</script>

<style scoped>
.marketing-page {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  max-width: 92.5rem;
  margin: 0 auto;
  padding: 1.5rem;
  color: var(--text);
  font-size: 1rem;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.primary-action,
.secondary-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.primary-action {
  color: #ffffff !important;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0;
}

.metric-card {
  display: flex;
  min-width: 0;
  min-height: 6.75rem;
  align-items: center;
  gap: 0.875rem;
  padding: 1.125rem;
  border: 1px solid var(--border);
  border-radius: 1.1875rem !important;
  background: var(--surface) !important;
  box-shadow: 0 0.75rem 1.875rem rgba(0, 0, 0, 0.1);
}

.metric-icon,
.editor-mark,
.surface-empty > span {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
}

.metric-icon {
  width: 2.625rem;
  height: 2.625rem;
  border-radius: 0.8125rem;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.065);
}

.metric-icon-green {
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.metric-icon-amber {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.metric-card > div {
  display: grid;
  min-width: 0;
}

.metric-card span,
.metric-card small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metric-card > div > span {
  color: var(--text-soft);
  font-size: 0.875rem;
  font-weight: 700;
}

.metric-card strong {
  color: #ffffff;
  font-size: 1.6875rem;
  line-height: 1.15;
}

.metric-card small {
  margin-top: 0.1875rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.content-surface {
  padding: 1.375rem;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 1.375rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
}

.archive-surface {
  margin-top: 1rem;
}

.surface-header,
.editor-header,
.section-heading,
.card-meta {
  display: flex;
  align-items: center;
}

.surface-header {
  justify-content: space-between;
  gap: 1.125rem;
  margin-bottom: 1.125rem;
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-header h2,
.editor-header h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.25rem;
  line-height: 1.25;
}

.surface-header p,
.editor-header p,
.section-heading p,
.surface-empty p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.count-pill,
.mini-chip,
.status-pill,
.archive-label {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
}

.count-pill {
  min-height: 1.75rem;
  padding: 0.25rem 0.625rem;
  border: 1px solid var(--border);
  color: var(--text-soft);
  background: rgba(255, 255, 255, 0.055);
  white-space: nowrap;
}

.content-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.875rem;
}

.content-card,
.archive-card {
  overflow: hidden;
  border: 1px solid var(--border);
  outline: none;
  background: rgba(255, 255, 255, 0.035) !important;
  transition: transform 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.content-card {
  border-radius: 1.125rem !important;
}

.content-card:focus-visible,
.archive-card:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.card-media {
  position: relative;
  height: 11.875rem;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.2);
}

.content-image {
  height: 100%;
}

.image-placeholder,
.archive-placeholder {
  display: grid;
  height: 100%;
  place-items: center;
  color: #77797f;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.035), rgba(0, 0, 0, 0.18));
}

.image-placeholder .v-icon {
  color: currentColor;
  font-size: 3rem;
}

.status-pill {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  min-height: 1.75rem;
  gap: 0.3125rem;
  padding: 0.25rem 0.625rem;
  color: #ffffff;
  backdrop-filter: blur(0.625rem);
}

.status-pill .v-icon {
  color: currentColor;
}

.is-visible {
  background: rgba(0, 150, 64, 0.88);
}

.is-upcoming {
  background: rgba(217, 119, 6, 0.9);
}

.is-past {
  background: rgba(82, 82, 91, 0.9);
}

.card-content {
  padding: 1rem;
}

.card-content h3,
.archive-copy h3,
.surface-empty h3,
.section-heading h3 {
  margin: 0;
  color: var(--text);
  font-size: 1rem;
  line-height: 1.35;
}

.studio-chips {
  display: flex;
  min-height: 2rem;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin: 0.875rem 0;
}

.mini-chip {
  min-height: 1.625rem;
  padding: 0.1875rem 0.5rem;
  border: 1px solid var(--border);
  color: #c7c7cb;
  background: rgba(255, 255, 255, 0.045);
}

.card-meta {
  justify-content: space-between;
  gap: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
  color: var(--muted);
}

.card-meta span {
  display: inline-flex;
  align-items: center;
  gap: 0.4375rem;
  font-size: 0.875rem;
}

.card-meta .v-icon {
  color: #8f9095;
}

.surface-empty {
  display: flex;
  min-height: 17rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 2.5rem;
  text-align: center;
}

.surface-empty > span {
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.875rem;
  border-radius: 1rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

.surface-empty > span .v-icon {
  color: currentColor;
}

.surface-empty .secondary-action {
  margin-top: 1rem;
}

.archive-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}

.archive-card {
  display: grid;
  grid-template-columns: 7.5rem minmax(0, 1fr);
  border-radius: 1rem !important;
  opacity: 0.82;
}

.archive-image,
.archive-placeholder {
  width: 7.5rem;
  height: 7.375rem;
}

.archive-copy {
  display: flex;
  min-width: 0;
  justify-content: center;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.875rem;
}

.archive-label {
  gap: 0.3125rem;
  color: var(--muted);
}

.archive-copy h3 {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.archive-pagination {
  margin-top: 1rem;
}

.editor-card {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  display: flex;
  width: 100%;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1.5rem !important;
  color: var(--text);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.018), transparent 38%), rgba(28, 28, 29, 0.985) !important;
  font-size: 1rem;
}

.editor-header {
  flex: 0 0 auto;
  gap: 0.875rem;
  padding: 1.25rem 1.375rem;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(115deg, rgba(0, 150, 64, 0.055), transparent 50%), rgba(255, 255, 255, 0.018);
}

.editor-mark {
  width: 3rem;
  height: 3rem;
  border: 1px solid rgba(0, 150, 64, 0.26);
  border-radius: 0.875rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.editor-mark .v-icon {
  color: currentColor;
}

.editor-header > div {
  min-width: 0;
}

.editor-close {
  margin-left: auto;
}

.editor-body {
  min-height: 0;
  flex: 1 1 auto;
  max-height: none;
  padding: 1.25rem !important;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.editor-section {
  height: 100%;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1.0625rem;
  background: rgba(255, 255, 255, 0.028);
}

.compact-section {
  height: auto;
}

.section-heading {
  align-items: flex-start;
  gap: 0.625rem;
  margin-bottom: 1rem;
}

.section-heading > .v-icon {
  color: #58d388;
}

.section-heading p {
  margin-top: 0.125rem;
}

.modern-field ::v-deep .v-input__slot {
  border-radius: 0.8125rem !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.modern-field ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.modern-field ::v-deep .v-label,
.editor-checkbox ::v-deep .v-label {
  color: #b8b8bc !important;
  font-size: 0.875rem;
}

.image-cropper {
  width: 100%;
  overflow: hidden;
  border-radius: 0.8125rem;
}

.format-hint,
.validation-hint {
  color: var(--muted);
  font-size: 0.75rem;
}

.format-hint {
  display: block;
  margin: 0.625rem 0 1rem;
  text-align: center;
}

.date-picker {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 0.8125rem;
  background: rgba(0, 0, 0, 0.12) !important;
}

.editor-actions {
  flex: 0 0 auto;
  min-height: 4.75rem;
  gap: 0.5rem;
  padding: 0.875rem 1.375rem !important;
  border-top: 1px solid var(--border);
  background: rgba(0, 0, 0, 0.14);
}

.cancel-action {
  min-height: 2.75rem;
  color: #ffffff !important;
  font-size: 0.875rem !important;
  text-transform: none;
}

@media (hover: hover) and (pointer: fine) {
  .content-card:hover,
  .archive-card:hover {
    transform: translateY(-2px);
    border-color: var(--border-strong);
    background: var(--surface-strong) !important;
  }
}

@media (max-width: 78.9375rem) {
  .marketing-page {
    padding: 1.125rem;
  }

  .content-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .archive-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 59.9375rem) {
  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 37.4375rem) {
  .marketing-page {
    padding: 0.75rem;
  }

  .metric-grid {
    gap: 0.5rem;
    margin: 0.75rem 0;
  }

  .metric-card {
    min-height: 6.5rem;
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    border-radius: 0.9375rem !important;
  }

  .metric-icon {
    width: 2.125rem;
    height: 2.125rem;
  }

  .metric-card small {
    display: none;
  }

  .content-surface {
    padding: 0.875rem;
    border-radius: 1.125rem !important;
  }

  .surface-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.625rem;
  }

  .content-grid,
  .archive-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .card-media {
    height: 11rem;
  }

  .editor-card {
    max-height: 100vh;
    max-height: 100dvh;
    border: 0;
    border-radius: 0 !important;
  }

  .editor-header {
    align-items: flex-start;
    padding: 1rem;
  }

  .editor-mark,
  .editor-header p,
  .validation-hint {
    display: none;
  }

  .editor-body {
    max-height: none;
    padding: 0.75rem !important;
  }

  .editor-section {
    padding: 0.875rem;
  }

  .editor-actions {
    flex-wrap: wrap;
    padding: 0.75rem 1rem max(0.75rem, env(safe-area-inset-bottom)) !important;
  }

  .editor-actions .primary-action {
    flex: 1 1 100%;
    order: 3;
  }
}
</style>
